export enum Status {
  IDLE = 'IDLE',
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export interface AppState {
  status: Status;
  file: File | null;
  redactedText: string | null;
  error: string | null;
  statusMessage: string;
  sourceName: string; // File name or "Pasted Text"
  inputMode: 'file' | 'text';
  processingPage?: number;
  processingTotalPages?: number;
  documentPageCount?: number;
  ocrPageFailures?: number[];
}

// Subscription Types — canonical values live in types/tiers.ts
import type { CanonicalTier } from './types/tiers';
export type SubscriptionTier = CanonicalTier;

export interface SubscriptionStatus {
  tier: CanonicalTier;
  expiresAt: number | null; // Unix timestamp, null for free tier
  isActive: boolean;
  cancelAtPeriodEnd?: boolean;
  status?: string;
}

export interface UsageLimits {
  maxFileSizeMB: number;
  maxPdfPages: number;
  maxRedactionsPerDay: number;
  hasWatermark: boolean;
}

// Tier limits configuration - updated with new limits
export const TIER_LIMITS: Record<CanonicalTier, UsageLimits> = {
  free: {
    maxFileSizeMB: 20,
    maxPdfPages: 3,
    maxRedactionsPerDay: 5, // Raised from 3 to 5
    hasWatermark: true,
  },
  day_pass: {
    maxFileSizeMB: 25,
    maxPdfPages: 50,
    maxRedactionsPerDay: Infinity,
    hasWatermark: false,
  },
  monthly_pro: {
    maxFileSizeMB: 50, // Raised from 25 to 50
    maxPdfPages: 100,  // Raised from 50 to 100
    maxRedactionsPerDay: Infinity,
    hasWatermark: false,
  },
  annual_pro: {
    maxFileSizeMB: 50,
    maxPdfPages: 100,
    maxRedactionsPerDay: Infinity,
    hasWatermark: false,
  },
  campus_monthly: {
    maxFileSizeMB: 50,
    maxPdfPages: 100,
    maxRedactionsPerDay: Infinity,
    hasWatermark: false,
  },
  campus_annual: {
    maxFileSizeMB: 50,
    maxPdfPages: 100,
    maxRedactionsPerDay: Infinity,
    hasWatermark: false,
  },
  district: {
    maxFileSizeMB: 50,
    maxPdfPages: 100,
    maxRedactionsPerDay: Infinity,
    hasWatermark: false,
  },
  // Complimentary pilot — Pro-equivalent limits; not sold via Stripe checkout
  educator_pilot: {
    maxFileSizeMB: 50,
    maxPdfPages: 100,
    maxRedactionsPerDay: Infinity,
    hasWatermark: false,
  },
};

// Daily usage tracking
export interface DailyUsage {
  count: number;
  date: string; // YYYY-MM-DD format
}
